import type { AuthSession } from '@kitchen-inventory/shared-types';

/**
 * Puerto de almacenamiento de sesión.
 *
 * Existe para que la decisión de *dónde* vive el token sea un detalle
 * intercambiable: hoy memoria + sessionStorage, mañana cookie httpOnly sin
 * tocar nada más que esta carpeta.
 */
export interface TokenStorage {
  readonly read: () => AuthSession | undefined;
  readonly write: (session: AuthSession) => void;
  readonly clear: () => void;
}

const REFRESH_KEY = 'kitchen.refresh.v1';

/**
 * El access token nunca toca el disco: vive solo en memoria, así un XSS no
 * puede leerlo de un almacén persistente. El refresh se guarda en
 * sessionStorage (muere al cerrar la pestaña, no se comparte entre ellas)
 * para poder rehidratar la sesión al recargar.
 */
export function createBrowserTokenStorage(
  store: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
): TokenStorage {
  let inMemory: AuthSession | undefined;

  return {
    read(): AuthSession | undefined {
      if (inMemory !== undefined) {
        return inMemory;
      }
      const refreshToken = safeRead(store);
      if (refreshToken === undefined) {
        return undefined;
      }
      // Sesión parcial: basta para pedir un access token nuevo.
      return {
        accessToken: '',
        refreshToken,
        expiresInSeconds: 0,
        user: { id: '', name: '', username: '', role: 'KITCHEN_MANAGER', permissions: [] },
      };
    },
    write(session): void {
      inMemory = session;
      try {
        store.setItem(REFRESH_KEY, session.refreshToken);
      } catch {
        // Navegación privada o almacenamiento bloqueado: la sesión sigue viva
        // en memoria durante esta pestaña.
      }
    },
    clear(): void {
      inMemory = undefined;
      try {
        store.removeItem(REFRESH_KEY);
      } catch {
        // Nada que limpiar si el almacén no está disponible.
      }
    },
  };
}

export function createMemoryTokenStorage(): TokenStorage {
  let session: AuthSession | undefined;
  return {
    read: () => session,
    write: (next) => {
      session = next;
    },
    clear: () => {
      session = undefined;
    },
  };
}

function safeRead(
  store: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>,
): string | undefined {
  try {
    return store.getItem(REFRESH_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}
